import React from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { userContext } from '../../../contexts/userContext'
import useAuthStore from '../../../stores/authStore'
import apiUserInfo from '../../../services/api/userInfo'
import LoadingLayoutWrapper from '../../../components/LoadingLayoutWrapper'
import PermissionsLoad from '../PermissionsLoad'

/**
 * Auth gate for protected routes (v2). Identity comes from /v2/auth/user-info
 * (the httpOnly token cookie is sent automatically), not from readable cookies.
 * While identity loads we show a spinner; a 401 there (no/expired session) sends
 * the user to /login. Capabilities are then loaded by PermissionsLoad.
 */
function PrivateRoute() {
  const user = React.useContext(userContext)
  const identity = useAuthStore((state) => state.identity)
  const hydrateFromUserInfo = useAuthStore((state) => state.hydrateFromUserInfo)
  const userId = identity?.id
  const [phase, setPhase] = React.useState(userId ? 'ready' : 'loading')

  React.useEffect(() => {
    if (userId) {
      setPhase('ready')
      return undefined
    }
    let active = true
    apiUserInfo
      .get()
      .then((info) => {
        if (!active) return
        hydrateFromUserInfo(info)
        setPhase(useAuthStore.getState().identity?.id ? 'ready' : 'unauthenticated')
      })
      .catch(() => {
        if (active) setPhase('unauthenticated')
      })
    return () => {
      active = false
    }
  }, [userId, hydrateFromUserInfo])

  // keep the legacy userContext populated for components still reading it
  React.useEffect(() => {
    if (identity) user.dispatch({ type: 'ADD_USER_COMPANY', payload: identity })
  }, [identity])

  if (phase === 'loading') return <LoadingLayoutWrapper />
  if (phase === 'unauthenticated' || !userId) return <Navigate to="/login" />
  return (
    <PermissionsLoad>
      <Outlet />
    </PermissionsLoad>
  )
}

export default PrivateRoute
